import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Mail, Info, Eye, Power, PowerOff, Code, Search, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { emailTemplatesApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import type { EmailTemplate, EmailTemplateListItem, CreateEmailTemplateRequest, UpdateEmailTemplateRequest, EmailTemplateCode } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

// Template code options grouped by category
const TEMPLATE_CODES: { value: EmailTemplateCode; label: string; category: string }[] = [
  // Billing
  { value: 'INVOICE_SENT', label: 'Invoice Sent', category: 'Billing' },
  { value: 'PAYMENT_REMINDER', label: 'Payment Reminder', category: 'Billing' },
  { value: 'PAYMENT_RECEIVED', label: 'Payment Received', category: 'Billing' },
  { value: 'INVOICE_OVERDUE', label: 'Invoice Overdue', category: 'Billing' },
  { value: 'QUOTE_SENT', label: 'Quote Sent', category: 'Billing' },
  // User Management
  { value: 'USER_WELCOME', label: 'User Welcome', category: 'User Management' },
  { value: 'PASSWORD_RESET', label: 'Password Reset', category: 'User Management' },
  { value: 'WELCOME', label: 'Welcome (Legacy)', category: 'User Management' },
  // CRM
  { value: 'CRM_LEAD_WELCOME', label: 'Lead Welcome', category: 'CRM' },
  { value: 'CRM_DEAL_PROPOSAL', label: 'Deal Proposal', category: 'CRM' },
  { value: 'CRM_FOLLOW_UP', label: 'Follow Up', category: 'CRM' },
  { value: 'CRM_DEAL_WON', label: 'Deal Won', category: 'CRM' },
  { value: 'CRM_DEAL_LOST', label: 'Deal Lost', category: 'CRM' },
  // Contracts
  { value: 'CONTRACT_SIGNING', label: 'Contract Signing Invitation', category: 'Contracts' },
  { value: 'CONTRACT_REMINDER', label: 'Contract Signing Reminder', category: 'Contracts' },
  { value: 'CONTRACT_SIGNED', label: 'Contract Fully Signed', category: 'Contracts' },
  // Custom
  { value: 'CUSTOM', label: 'Custom', category: 'Other' },
]

// Sample data for preview
const SAMPLE_DATA: Record<string, string> = {
  // Client placeholders
  '{{CLIENT_NAME}}': 'John Smith',
  '{{CLIENT_EMAIL}}': 'john.smith@example.com',
  '{{CLIENT_PHONE}}': '+1 (555) 987-6543',
  '{{CLIENT_ADDRESS}}': '456 Client Avenue, New York, NY 10001',
  // Invoice placeholders
  '{{INVOICE_NUMBER}}': 'INV-2024-0001',
  '{{INVOICE_DATE}}': 'November 27, 2024',
  '{{DUE_DATE}}': 'December 27, 2024',
  '{{TOTAL_AMOUNT}}': '$1,500.00',
  '{{BALANCE_DUE}}': '$500.00',
  '{{CURRENCY}}': 'USD',
  // Company placeholders
  '{{COMPANY_NAME}}': 'Acme Corporation',
  '{{COMPANY_EMAIL}}': 'billing@acme.com',
  '{{COMPANY_PHONE}}': '+1 (555) 123-4567',
  '{{COMPANY_ADDRESS}}': '123 Business Street, San Francisco, CA 94102',
  '{{COMPANY_LOGO}}': 'https://example.com/logo.png',
  // Payment placeholders
  '{{PAYMENT_LINK}}': 'https://pay.example.com/inv123',
  '{{PAYMENT_TERMS}}': 'Net 30',
  '{{BANK_DETAILS}}': 'Bank: ABC Bank, Account: 1234567890',
  // User placeholders
  '{{USER_NAME}}': 'Jane Smith',
  '{{USER_EMAIL}}': 'jane.smith@example.com',
  '{{USER_LOGIN_ID}}': 'jsmith',
  '{{SET_PASSWORD_LINK}}': 'https://app.example.com/set-password?token=abc123',
  '{{RESET_PASSWORD_LINK}}': 'https://app.example.com/reset-password?token=xyz789',
  // CRM Lead placeholders
  '{{LEAD_NAME}}': 'Michael Johnson',
  '{{LEAD_EMAIL}}': 'michael.johnson@example.com',
  '{{LEAD_PHONE}}': '+1 (555) 234-5678',
  '{{LEAD_COMPANY}}': 'Johnson Industries',
  '{{LEAD_SOURCE}}': 'Website',
  '{{LEAD_STATUS}}': 'Qualified',
  // CRM Deal placeholders
  '{{DEAL_NAME}}': 'Website Redesign Project',
  '{{DEAL_VALUE}}': '$25,000.00',
  '{{DEAL_STAGE}}': 'Proposal',
  '{{EXPECTED_CLOSE_DATE}}': 'January 15, 2025',
  '{{PIPELINE_NAME}}': 'Sales Pipeline',
  // CRM Contact placeholders
  '{{CONTACT_NAME}}': 'Sarah Williams',
  '{{CONTACT_EMAIL}}': 'sarah.williams@example.com',
  // CRM Assignment placeholders
  '{{ASSIGNED_USER_NAME}}': 'Mike Johnson',
  '{{ASSIGNED_USER_EMAIL}}': 'mike@company.com',
  // Contract placeholders
  '{{CONTRACT_TITLE}}': 'Service Agreement 2024',
  '{{CONTRACT_NUMBER}}': 'CON-2024-0001',
  '{{SIGNER_NAME}}': 'Robert Brown',
  '{{SIGNER_EMAIL}}': 'robert.brown@example.com',
  '{{SIGNING_LINK}}': 'https://app.example.com/sign/abc123',
  '{{EXPIRES_AT}}': 'January 15, 2025',
  '{{COMPLETED_AT}}': 'December 20, 2024',
}

// Function to replace placeholders with sample data for preview
function processTemplateForPreview(html: string): string {
  let processed = html
  Object.entries(SAMPLE_DATA).forEach(([placeholder, value]) => {
    processed = processed.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value)
  })
  return processed
}

const DEFAULT_BODY_HTML = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h2 style="color: #333;">{{INVOICE_NUMBER}}</h2>
  <p>Dear {{CLIENT_NAME}},</p>
  <p>Your email content goes here...</p>
  <p>Best regards,<br>{{COMPANY_NAME}}</p>
</div>`

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

export default function EmailTemplatesPage() {
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [viewingTemplate, setViewingTemplate] = useState<EmailTemplate | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [showPlaceholderHelp, setShowPlaceholderHelp] = useState(false)
  const [showHtmlSource, setShowHtmlSource] = useState(false)
  const [formData, setFormData] = useState<CreateEmailTemplateRequest>({
    template_name: '',
    template_code: 'CUSTOM',
    description: '',
    subject: '',
    body_html: DEFAULT_BODY_HTML,
    is_enabled: true,
  })

  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: emailTemplatesApi.getAll,
  })

  // Filter and paginate templates
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesSearch = searchQuery === '' ||
        template.template_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()))

      const templateCategory = TEMPLATE_CODES.find((c) => c.value === template.template_code)?.category || 'Other'
      const matchesCategory = categoryFilter === 'all' || templateCategory === categoryFilter

      return matchesSearch && matchesCategory
    })
  }, [templates, searchQuery, categoryFilter])

  const totalPages = Math.ceil(filteredTemplates.length / rowsPerPage)
  const paginatedTemplates = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage
    return filteredTemplates.slice(startIndex, startIndex + rowsPerPage)
  }, [filteredTemplates, currentPage, rowsPerPage])

  // Reset to first page when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value)
    setCurrentPage(1)
  }

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(Number(value))
    setCurrentPage(1)
  }

  // Fetch placeholders
  const { data: placeholders = [] } = useQuery({
    queryKey: ['email-template-placeholders'],
    queryFn: emailTemplatesApi.getPlaceholders,
  })

  // Helper to extract error message as string
  const getErrorMessage = (error: any, fallback: string): string => {
    const errorMessage = error.response?.data?.message
    if (typeof errorMessage === 'string') return errorMessage
    if (typeof error.message === 'string') return error.message
    return fallback
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: emailTemplatesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      toast.success('Email template created successfully')
      handleCloseDialog()
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to create template'))
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEmailTemplateRequest }) =>
      emailTemplatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      toast.success('Email template updated successfully')
      handleCloseDialog()
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update template'))
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: emailTemplatesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      toast.success('Email template deleted successfully')
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to delete template'))
    },
  })

  // Toggle enabled mutation
  const toggleMutation = useMutation({
    mutationFn: emailTemplatesApi.toggleEnabled,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      toast.success('Template status updated')
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to toggle template'))
    },
  })

  // Initialize defaults mutation
  const initializeMutation = useMutation({
    mutationFn: emailTemplatesApi.initializeDefaults,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      toast.success(`${data.count} default templates created`)
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to initialize templates'))
    },
  })

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingTemplate(null)
    setShowHtmlSource(false)
    setFormData({
      template_name: '',
      template_code: 'CUSTOM',
      description: '',
      subject: '',
      body_html: DEFAULT_BODY_HTML,
      is_enabled: true,
    })
  }

  const handleView = async (template: EmailTemplateListItem) => {
    try {
      const fullTemplate = await emailTemplatesApi.getById(template.template_id)
      setViewingTemplate(fullTemplate)
      setIsViewDialogOpen(true)
    } catch {
      toast.error('Failed to load template details')
    }
  }

  const handleEdit = async (template: EmailTemplateListItem) => {
    try {
      const fullTemplate = await emailTemplatesApi.getById(template.template_id)
      setEditingTemplate(fullTemplate)
      setFormData({
        template_name: fullTemplate.template_name,
        template_code: fullTemplate.template_code,
        description: fullTemplate.description || '',
        subject: fullTemplate.subject,
        body_html: fullTemplate.body_html,
        is_enabled: fullTemplate.is_enabled,
      })
      setIsDialogOpen(true)
    } catch {
      toast.error('Failed to load template details')
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingTemplate) {
      updateMutation.mutate({
        id: editingTemplate.template_id,
        data: formData,
      })
    } else {
      createMutation.mutate(formData)
    }
  }

  const insertPlaceholder = (placeholder: string) => {
    setFormData(prev => ({
      ...prev,
      body_html: prev.body_html + placeholder,
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Email Templates</h1>
          <p className="text-muted-foreground">
            Manage email templates for invoices, reminders, and notifications
          </p>
        </div>
        {canAddEdit('EMAIL_TEMPLATES') && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => initializeMutation.mutate()}
              disabled={initializeMutation.isPending}
            >
              {initializeMutation.isPending ? 'Initializing...' : 'Initialize Missing Templates'}
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
              <DialogTrigger asChild>
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Template
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
                </DialogTitle>
                <DialogDescription>
                  {editingTemplate
                    ? 'Update email template settings and content'
                    : 'Create a new email template with HTML content'}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Tabs defaultValue="basic" className="mt-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basic Settings</TabsTrigger>
                    <TabsTrigger value="content">
                      <Mail className="mr-2 h-4 w-4" />
                      Email Content
                    </TabsTrigger>
                    <TabsTrigger value="preview">
                      <Eye className="mr-2 h-4 w-4" />
                      Preview
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4 mt-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="template_name">Template Name *</Label>
                        <Input
                          id="template_name"
                          value={formData.template_name}
                          onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                          placeholder="e.g., Invoice Sent Email"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="template_code">Template Type *</Label>
                        <Select
                          value={formData.template_code}
                          onValueChange={(value: EmailTemplateCode) =>
                            setFormData({ ...formData, template_code: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select template type" />
                          </SelectTrigger>
                          <SelectContent>
                            {['Billing', 'User Management', 'CRM', 'Contracts', 'Other'].map((category) => (
                              <SelectGroup key={category}>
                                <SelectLabel>{category}</SelectLabel>
                                {TEMPLATE_CODES.filter((code) => code.category === category).map((code) => (
                                  <SelectItem key={code.value} value={code.value}>
                                    {code.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Input
                        id="description"
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description of when this template is used"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="is_enabled"
                        checked={formData.is_enabled}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                      />
                      <Label htmlFor="is_enabled">Enable this template</Label>
                    </div>
                  </TabsContent>

                  <TabsContent value="content" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="subject">Email Subject *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="e.g., Invoice {{INVOICE_NUMBER}} from {{COMPANY_NAME}}"
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        You can use placeholders like {'{{CLIENT_NAME}}'} in the subject
                      </p>
                    </div>

                    {/* Placeholder Help */}
                    <Collapsible open={showPlaceholderHelp} onOpenChange={setShowPlaceholderHelp}>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm" type="button">
                          <Info className="mr-2 h-4 w-4" />
                          {showPlaceholderHelp ? 'Hide' : 'Show'} Available Placeholders
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2">
                        <Card>
                          <CardContent className="pt-4">
                            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3 text-sm">
                              {placeholders.map((p) => (
                                <div
                                  key={p.placeholder}
                                  className="p-2 border rounded cursor-pointer hover:bg-accent"
                                  onClick={() => insertPlaceholder(p.placeholder)}
                                  title={`Click to insert. Example: ${p.example}`}
                                >
                                  <code className="text-xs font-mono text-primary">{p.placeholder}</code>
                                  <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </CollapsibleContent>
                    </Collapsible>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="body_html">Email Body *</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowHtmlSource(!showHtmlSource)}
                        >
                          <Code className="mr-2 h-4 w-4" />
                          {showHtmlSource ? 'Visual Editor' : 'HTML Source'}
                        </Button>
                      </div>
                      {showHtmlSource ? (
                        <Textarea
                          id="body_html"
                          value={formData.body_html}
                          onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                          placeholder="Enter HTML content for the email body..."
                          className="font-mono text-sm min-h-[300px]"
                        />
                      ) : (
                        <RichTextEditor
                          content={formData.body_html}
                          onChange={(html) => setFormData({ ...formData, body_html: html })}
                          placeholder="Start typing your email content..."
                          className="min-h-[300px]"
                        />
                      )}
                      <p className="text-xs text-muted-foreground">
                        {showHtmlSource
                          ? 'Edit raw HTML directly. Switch to Visual Editor for WYSIWYG editing.'
                          : 'Use the toolbar to format your email. Click on placeholders above to insert them, or switch to HTML Source for direct editing.'}
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="preview" className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Email Preview</CardTitle>
                        <CardDescription>
                          Preview how your email will look with sample data
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="p-3 bg-muted rounded-lg">
                            <p className="text-sm">
                              <strong>Subject:</strong>{' '}
                              {processTemplateForPreview(formData.subject)}
                            </p>
                          </div>
                          <div
                            className="border rounded-lg p-4 bg-white"
                            dangerouslySetInnerHTML={{
                              __html: processTemplateForPreview(formData.body_html),
                            }}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? 'Saving...'
                      : editingTemplate
                      ? 'Update Template'
                      : 'Create Template'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* View Template Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => {
        setIsViewDialogOpen(open)
        if (!open) setViewingTemplate(null)
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>View Email Template</DialogTitle>
            <DialogDescription>
              {viewingTemplate?.template_name}
            </DialogDescription>
          </DialogHeader>
          {viewingTemplate && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground">Template Code</Label>
                  <p className="font-medium">{viewingTemplate.template_code}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p>
                    <Badge variant={viewingTemplate.is_enabled ? 'default' : 'secondary'}>
                      {viewingTemplate.is_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </p>
                </div>
              </div>
              {viewingTemplate.description && (
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p>{viewingTemplate.description}</p>
                </div>
              )}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Email Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm">
                        <strong>Subject:</strong>{' '}
                        {processTemplateForPreview(viewingTemplate.subject)}
                      </p>
                    </div>
                    <div
                      className="border rounded-lg p-4 bg-white"
                      dangerouslySetInnerHTML={{
                        __html: processTemplateForPreview(viewingTemplate.body_html),
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates Table */}
      <Card className="flex flex-col h-[calc(100vh-280px)]">
        <CardHeader className="flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>
                Configure email templates for different scenarios
              </CardDescription>
            </div>
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9 w-full sm:w-[250px]"
                />
              </div>
              <Select value={categoryFilter} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Billing">Billing</SelectItem>
                  <SelectItem value="User Management">User Management</SelectItem>
                  <SelectItem value="CRM">CRM</SelectItem>
                  <SelectItem value="Contracts">Contracts</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>No email templates found.</p>
              <p className="text-sm">Create your first template or initialize default templates.</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>No templates match your search.</p>
              <p className="text-sm">Try adjusting your search or filter criteria.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTemplates.map((template) => (
                  <TableRow key={template.template_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{template.template_name}</p>
                        {template.description && (
                          <p className="text-xs text-muted-foreground">{template.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="outline">
                          {TEMPLATE_CODES.find((c) => c.value === template.template_code)?.label ||
                            template.template_code}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {TEMPLATE_CODES.find((c) => c.value === template.template_code)?.category || 'Other'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {template.subject}
                    </TableCell>
                    <TableCell>
                      <Badge variant={template.is_enabled ? 'default' : 'secondary'}>
                        {template.is_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(template)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </DropdownMenuItem>
                          {canAddEdit('EMAIL_TEMPLATES') && (
                            <>
                              <DropdownMenuItem onClick={() => toggleMutation.mutate(template.template_id)}>
                                {template.is_enabled ? (
                                  <>
                                    <PowerOff className="mr-2 h-4 w-4" />
                                    Disable
                                  </>
                                ) : (
                                  <>
                                    <Power className="mr-2 h-4 w-4" />
                                    Enable
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(template)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this template?')) {
                                    deleteMutation.mutate(template.template_id)
                                  }
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {/* Pagination Footer */}
        {filteredTemplates.length > 0 && (
          <div className="flex-shrink-0 border-t px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Rows per page:</span>
              <Select value={rowsPerPage.toString()} onValueChange={handleRowsPerPageChange}>
                <SelectTrigger className="w-[70px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="ml-2">
                {((currentPage - 1) * rowsPerPage) + 1}-{Math.min(currentPage * rowsPerPage, filteredTemplates.length)} of {filteredTemplates.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  )
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}

